/**
 * DocumentStateService — display-layer adapter for the web-app ProseMirror editor.
 *
 * In the [e]→[p]→[s] architecture this service is [s] for the web app.
 * It owns NO state of its own.  All state lives in ProcessingPipelineService
 * (the MultiElementDocumentStore keyed by PROSEMIRROR_ELEMENT_ID).  This
 * service is a thin adapter that:
 *
 *   - exposes `doc$` / `docState$` observables wired directly to the pipeline
 *   - provides synchronous `getDoc()` / `getState()` that read from the pipeline
 *   - tracks cursor position (unrelated to spelling state)
 *
 * There is exactly one copy of the document state.  The pipeline writes it;
 * display consumers (components, other services) read it here.  There is no
 * possibility of the display being out of sync with the pipeline.
 *
 * updateDoc() and updateState() are intentionally removed.  If you are hitting
 * a compile error here, the caller must be updated to write state through the
 * pipeline (ProcessingPipelineService.process()) rather than directly.
 *
 * Chrome-extension note: app-page.component.ts still calls updateState() on
 * the CSW-1349 branch.  That branch must migrate to the pipeline write path
 * before merging here.
 */

import { Injectable, OnDestroy } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';

import { documentDTO, editStateEnum, paragraphDTO } from '@capiche/ts-data-models-shared';
import { CapicheLoggerService } from 'capiche-logger';
import { ProcessingPipelineService } from '../processing-pipeline/processing-pipeline.service';

// The initial state returned synchronously before the pipeline emits for the
// first time (e.g. on a fresh editor before any content has been typed).
const emptyDoc: documentDTO = {
  input: '',
  index: 0,
  length: 2,
  editState: editStateEnum.checked,
  paragraphs: [
    {
      input: '',
      index: 0,
      length: 2,
      editState: editStateEnum.checked,
      sentences: [],
    },
  ],
};

// The element ID used by the web-app ProseMirror editor.
//
// TODO: this constant (and DocumentStateService itself) belongs in the web app, not in the
// capiche-text-processor library.  DocumentStateService is hardwired to a single element ID
// and is not usable by the Chrome extension (which has dynamic per-element state).  When the
// Chrome extension pipeline work begins (CSW-1349 migration), refactor by:
//   - moving PROSEMIRROR_ELEMENT_ID to the web app (e.g. prose-mirror-editor/editor-config.ts)
//   - accepting the element ID via an Angular InjectionToken so DocumentStateService is
//     no longer tied to a specific editor, OR moving DocumentStateService to the web app entirely.
export const PROSEMIRROR_ELEMENT_ID = 'prosemirror-doc';

@Injectable({
  providedIn: 'root',
})
export class DocumentStateService implements OnDestroy {
  logger: CapicheLoggerService = new CapicheLoggerService('document-state.service:', false);

  // ── Observables wired directly to the pipeline ─────────────────────────────

  /** Full document observable. Emits the current value immediately. */
  public readonly doc$: Observable<documentDTO> = this.pipeline.getDoc$(PROSEMIRROR_ELEMENT_ID);

  /**
   * Paragraphs slice — backwards-compatible observable for existing callers.
   * Emits the paragraphs array whenever the document state changes.
   */
  public readonly docState$: Observable<paragraphDTO[]> = this.doc$.pipe(map((doc) => doc.paragraphs));

  // ── Cursor position (unrelated to spelling state) ───────────────────────────
  private cursorPosition = new BehaviorSubject<number>(0);
  cursorPosition$ = this.cursorPosition.asObservable().pipe(debounceTime(250), distinctUntilChanged());

  constructor(private readonly pipeline: ProcessingPipelineService) {
    this.logger.log('constructor');
  }

  ngOnDestroy(): void {
    // Nothing to unsubscribe — getDoc$() subscribers manage their own lifecycles.
  }

  // ── Read API ────────────────────────────────────────────────────────────────

  /** Return the current full document state. */
  public getDoc(): documentDTO {
    return this.pipeline.getDoc(PROSEMIRROR_ELEMENT_ID) ?? emptyDoc;
  }

  /** Backwards-compatible — returns the current paragraphs array. */
  public getState(): paragraphDTO[] {
    return this.getDoc().paragraphs;
  }

  public setCursorPosition(position: number) {
    this.logger.log('setCursorPosition', position);
    this.cursorPosition.next(position);
  }

  public getCursorPosition(): number {
    this.logger.log('getCursorPosition', this.cursorPosition.getValue());
    return this.cursorPosition.getValue();
  }
}
